<template>
  <div v-if="resource" class="product-details">
    <div class="product-details__panel" :class="{ open: productDetailsState }">
      <!-- Title and close button -->
      <div class="product-details__header">
        <h1 class="product-details__title main-title">
          <LockIcon :published-to="publishedTo" />
          {{ title }}
        </h1>
        <i class="icon-cancel" @click="closeProductDetails()"></i>
      </div>

      <!-- Image and description -->
      <div class="product-details__content">
        <div class="product-details__preview">
          <div v-if="resourceType === resourceTypes.VIDEO" class="video-player" @click="play($event)">
            <span class="icon-player"></span>
            <video width="100%" max-height="100%" @ended="onEnd">
              <source :src="baseUrlMedia + resource.item.fileId + '/' + resource.item.fileName" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <img
            v-else
            :src="filePreviewPath + filePathExtension"
            @error="handleImageError"
            :alt="setProperFieldExternalFirst(resource.item, 'title') || 'image'"
          />
          <a :href="filePreviewPath" class="product-details__button button" target="_blank">
            <i class="icon-external-link"></i>
          </a>
        </div>
        <div class="product-details__description">
          <p v-if="descriptionExternal" v-html="descriptionExternal"></p>
          <p v-if="descriptionInternal" class="internal" v-html="descriptionInternal"></p>
          <p v-if="calculationExternal || calculationInternal" class="sub-heading" v-html="langJson[lang].calculations"></p>
          <p v-if="calculationExternal" v-html="calculationExternal"></p>
          <p v-if="calculationInternal" class="internal" v-html="calculationInternal"></p>
        </div>
      </div>

      <!-- Download section -->
      <div class="product-details__download">
        <div class="product-details__download-header">{{ langJson[lang].download }}</div>
        <div v-if="resource.items.length > 1" class="product-details__download-buttons">
          <div v-for="(item, innerIndex) in resource.items" :key="'file-extension-' + innerIndex">
            <a
              :href="`${baseUrlMedia}${item.fileId}/${item.fileName}${item.extension === 'dwg' ? '' : '/Download'}`"
              class="button button_hollow button_icon-left"
            >
              <i class="icon-download"></i>
              <span>{{ item.extension }}</span>
            </a>
          </div>
          <div>
            <a
              :href="`${baseUrlMedia}zip/?files=${prepareZipLink(resource)}&name=bundle.zip`"
              class="button button_icon-left"
              @click="setDataLayers()"
            >
              <i class="icon-download"></i>
              zip
            </a>
          </div>
        </div>

        <a
          v-else
          :href="`${baseUrlMedia}${resource.item.fileId}/${resource.item.fileName}${resource.item.extension === 'dwg' ? '' : '/Download'}`"
          class="button button_hollow button_icon-left"
        >
          <i class="icon-download"></i>
          <span>{{ resource.item.extension }}</span>
        </a>
      </div>
    </div>
    <overlay
      :isOverlayVisible="productDetailsState == true"
      :isOverlayFullscreen="productDetailsState == true"
      @onOverlayClicked="closeProductDetails()"
    />
  </div>
</template>

<script setup lang="ts">
  import Overlay from '~/components/ui/Overlay.vue';
  import { useMainStore } from '@/stores/main';
  import { resourceTypes } from '@/constants';
  import { handleImageError } from '~/utils/imageFallback';

  // Define interfaces
  interface ResourceItem {
    fileId?: string;
    fileName?: string;
    title?: string;
    description?: string;
    calculation?: string;
    publishedTo?: number;
    mimeType?: string;
    extension?: string;
    coverImageId?: string;
    coverImageName?: string;
    external?: {
      [key: string]: any;
    };
    internal?: {
      [key: string]: any;
    };
  }

  interface ResourceObject {
    item: ResourceItem;
    items: ResourceItem[];
  }

  const mainStore = useMainStore();
  const baseUrlMedia = computed(() => mainStore.baseUrlMedia);
  const langJson = computed(() => mainStore.langJson);
  const lang = computed(() => mainStore.lang);
  const productDetailsState = computed(() => mainStore.productDetailsState);
  const resource = computed(() => mainStore.resource as ResourceObject);

  function closeProductDetails() {
    mainStore.closeProductDetails();
  }

  function setDataLayers() {
    // Implement as needed or port from Vuex action
  }

  function setProperFieldExternal(obj: ResourceItem | undefined, field: string): string {
    if (!obj) return '';
    return obj.external?.[field] || (obj as any)[field] || '';
  }

  function setProperFieldInternal(obj: ResourceItem | undefined, field: string): string {
    if (!obj) return '';
    return obj.internal?.[field] || '';
  }

  function setProperFieldExternalFirst(obj: ResourceItem | undefined, field: string): string {
    if (!obj) return '';
    return obj.external && obj.external[field] ? obj.external[field] : obj.internal && obj.internal[field] ? obj.internal[field] : '';
  }

  const title = computed(() => setProperFieldExternalFirst(resource.value?.item, 'title'));
  const publishedTo = computed(() => resource.value?.item?.publishedTo);
  const descriptionExternal = computed(() => setProperFieldExternal(resource.value?.item, 'description'));
  const descriptionInternal = computed(() => setProperFieldInternal(resource.value?.item, 'description'));
  const calculationExternal = computed(() => setProperFieldExternal(resource.value?.item, 'calculation'));
  const calculationInternal = computed(() => setProperFieldInternal(resource.value?.item, 'calculation'));

  function getResourceType(mimeType: string = '', extension: string = ''): string {
    const fileExtensions = ['pdf', 'dwg', 'doc', 'xls'];
    const videoMimeTypes = ['application/octet-stream'];
    if (mimeType.indexOf('image') > -1) {
      return resourceTypes.IMAGE;
    } else if (videoMimeTypes.includes(mimeType) && !fileExtensions.includes(extension)) {
      return resourceTypes.VIDEO;
    } else {
      return resourceTypes.FILE;
    }
  }

  const resourceType = computed(() => getResourceType(resource.value?.item?.mimeType, resource.value?.item?.extension));
  const isImage = computed(() => resourceType.value === resourceTypes.IMAGE);
  const isVideo = computed(() => resourceType.value === resourceTypes.VIDEO);
  const filePathExtension = computed(() => (isImage.value ? '?width=800&height=800' : '/Preview?mimetype=image/jpg'));
  const filePreviewPath = computed(() => {
    const item = resource.value?.item;
    if (!item) return '';

    if (item.coverImageId) {
      return baseUrlMedia.value + item.coverImageId + '/' + item.coverImageName;
    }
    return baseUrlMedia.value + item.fileId + '/' + item.fileName;
  });

  function prepareZipLink(resourceObj: ResourceObject): string {
    let link = '';
    resourceObj.items.forEach((element, index) => {
      link += `${element.fileId}%2F${element.fileName}`;
      if (index !== resourceObj.items.length - 1) {
        link += '%2C';
      }
    });
    return link;
  }

  function play(event: Event) {
    const target = event.target as HTMLElement;
    const videoElement = target.querySelector('video') as HTMLVideoElement;

    if (isVideo.value && videoElement?.paused) {
      target.classList.add('playing');
      videoElement.play();
    } else if (isVideo.value && videoElement) {
      target.classList.remove('playing');
      videoElement.pause();
    }
  }

  function onEnd(event: Event) {
    const target = event.target as HTMLVideoElement;
    target.parentElement?.classList.remove('playing');
  }
</script>
